import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import {
  distinctUntilChanged, filter, map, pluck, skip, switchMap, takeUntil, tap
} from 'rxjs/operators';

import { GetEntitiesForMapViewGQL } from 'generated/graphql';

import { CAMPAIGN_ID_CHANGED } from 'app/_state/campaign-context/campaign-context-actions';
import { DefaultRulesetService } from 'app/query-builder/core/get-default-ruleset.service';
import {
  IQueryStorageItem, QueryStorageService
} from 'app/query-builder/core/query-storage.service';
import { isNilOrEmpty } from 'helpers';
import { SimpleQueryReducer } from 'query-builder/core/simple-query-reducer';

import { IEntityMapViewQueryParams } from '../pages/entity-map-view-page/entity-map-view.page';
import * as ACTIONS from './entity-map-view.actions';
import { SELECT_CURRENT_QUERY, SELECT_URL_AND_PARAMS } from './entity-map-view.selectors';

@Injectable()
export class EntityMapViewEffects {
  private queryParams$ = this._store.select(
    SELECT_URL_AND_PARAMS
  ).pipe(
    filter(c => c.url && c.url.includes('entity/map-view')),
    map(c => c.queryParams),
    map(params => {
      const defaultParams = {
        layer: 'all',
        clientQueryId: null
      };
      return Object.assign({}, defaultParams, params) as IEntityMapViewQueryParams;
    })
  );

  resetOnCampaignChanged$ = createEffect(() => this.actions$.pipe(ofType(
    CAMPAIGN_ID_CHANGED
  )).pipe(
    filter(payload => !isNilOrEmpty(payload.campaignId)),
    switchMap((payload) => this._store.select(SELECT_CURRENT_QUERY).pipe(
      map(cq => cq?.campaignId !== payload.campaignId)
    )),
    filter(shouldReset => shouldReset),
    map(() => ACTIONS.RESET_MAP_STATE())
  ));

  setCurrentQuery$ = createEffect(
    () => combineLatest([
      this.queryParams$.pipe(
        pluck('campaignId'),
        distinctUntilChanged()
      ),
      this.queryParams$.pipe(
        pluck('clientQueryId'),
        distinctUntilChanged()
      )
    ])
      .pipe(
        switchMap(([campaignId, clientQueryId]) => this.getQueryOrDefault(campaignId, clientQueryId)),
        map(qsi => ACTIONS.SET_CURRENT_QUERY({ payload: qsi }))
      ),
    { dispatch: true }
  );

  watchQueryChange$ = createEffect(
    () => this._store.select(SELECT_CURRENT_QUERY).pipe(
      filter(r => !!r),
      switchMap((currentQuery) => {
        this._store.dispatch(ACTIONS.DATA_LOADING());
        const reducedQuery = new SimpleQueryReducer(currentQuery.query).reduce();
        return this._getEntitiesForMapViewGQL.watch(
          {
            campaignId: currentQuery.campaignId,
            name: currentQuery.clientQueryId,
            body: JSON.stringify(reducedQuery),
            first: 5000
          }, {
          // fetchPolicy: 'cache-and-network'
        }).valueChanges.pipe(
          filter(d => !isNilOrEmpty(d.data)),
          map(r => r.data),
          takeUntil(
            // Unwatch the query once it changes
            this._store.select(SELECT_CURRENT_QUERY).pipe(
              skip(1)
            )
          ),
        );
      }),
      map((data) => ACTIONS.ENTITIES_FETCHED({ payload: data }))
    )
  );

  onRunQueryClicked$ = createEffect(
    () => this.actions$.pipe(
      ofType(ACTIONS.RUN_QUERY_CLICKED),
      map(value => value.payload),
      tap((payload) => {
        this._store.dispatch(ACTIONS.SET_SELECTED_MARKER({ payload: null }));
        const query = this._queryStorageService.saveQuery(payload.campaignId, payload.ruleSet);
        // Will trigger effects listening for this change
        this.updateRouteParams({
          clientQueryId: query.clientQueryId
        });
      })
    ), {
    dispatch: false
  }
  );

  onResetQueryClicked$ = createEffect(
    () => this.actions$.pipe(
      ofType(ACTIONS.RESET_QUERY_CLICKED),
      tap((payload) => {
        this._store.dispatch(ACTIONS.SET_SELECTED_MARKER({ payload: null }));
        // Will trigger effects listening for this change
        this.updateRouteParams({
          clientQueryId: null
        });
      })
    ), {
    dispatch: false
  }
  );

  /**
   *
   */
  constructor(
    private actions$: Actions,
    private _store: Store,
    private _getEntitiesForMapViewGQL: GetEntitiesForMapViewGQL,
    private _defaultRulesetService: DefaultRulesetService,
    private _queryStorageService: QueryStorageService,
    private _router: Router
  ) {
  }

  private updateRouteParams(queryParams) {
    this._router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  private getQueryOrDefault(campaignId: string, clientQueryId: string): Observable<IQueryStorageItem> {
    const currentQuery = this._queryStorageService.getQuery(clientQueryId, campaignId);
    if (isNilOrEmpty(currentQuery)) {
      return this._defaultRulesetService
        .getDefaultEntityTypeScopedRuleset(campaignId)
        .pipe(
          map(rs => ({
            campaignId,
            clientQueryId: null,
            query: rs,
            updated_at: null
          }))
        );
    }

    return of(currentQuery);
  }

}
